import * as React from "react";
import { FileUp, Loader2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  uploadDDQ,
  type DDQUploadResponse,
  type Fund,
} from "@/lib/api";

const ACCEPTED_EXTS = [".docx", ".xlsx", ".pdf"];
const ACCEPTED_HINT = "Accepted: .docx, .xlsx, .pdf · up to 10 MB";

export function DDQUploadDialog({
  open,
  fund,
  onClose,
  onUploaded,
}: {
  open: boolean;
  fund: Fund | null;
  onClose: () => void;
  onUploaded: (resp: DDQUploadResponse) => void;
}) {
  const [file, setFile] = React.useState<File | null>(null);
  const [name, setName] = React.useState<string>("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!open) {
      setFile(null);
      setName("");
      setBusy(false);
      setError(null);
      setDragOver(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, busy]);

  if (!open) return null;

  const accept = ACCEPTED_EXTS.join(",");

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) chooseFile(f);
  };

  const chooseFile = (f: File) => {
    const lower = f.name.toLowerCase();
    if (!ACCEPTED_EXTS.some((ext) => lower.endsWith(ext))) {
      setError("Unsupported file type. Upload a .docx, .xlsx, or .pdf DDQ.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError("File is too large (max 10 MB).");
      return;
    }
    setError(null);
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^.]+$/, ""));
  };

  const submit = async () => {
    if (!file || !fund || busy) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await uploadDDQ(file, fund.id, name.trim() || file.name);
      onUploaded(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-md border bg-card p-5 shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold">Upload DDQ</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Upload your Due Diligence Questionnaire. We&rsquo;ll parse it and
              draft answers from the {fund?.name || "selected"} data room before
              you review.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Close upload dialog"
            onClick={onClose}
            disabled={busy}
          >
            <X />
          </Button>
        </div>

        <div className="mt-4 flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Fund:</span>
          {fund ? (
            <Badge variant="outline" className="font-semibold">
              {fund.name}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-destructive">
              No fund selected
            </Badge>
          )}
        </div>

        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`mt-4 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed p-6 text-center transition-colors ${
            dragOver ? "border-primary bg-accent" : "border-border bg-background"
          }`}
        >
          <FileUp className="size-7 text-brand" />
          <div className="text-sm font-medium">
            {file ? file.name : "Click to choose or drop a file"}
          </div>
          <div className="text-xs text-muted-foreground">{ACCEPTED_HINT}</div>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) chooseFile(f);
            }}
          />
        </div>

        <div className="mt-4">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="ddq-name">
            DDQ name (optional)
          </label>
          <input
            id="ddq-name"
            type="text"
            placeholder="e.g. Acme Pension Fund Diligence"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/40"
          />
        </div>

        {error ? (
          <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
            {error}
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={!file || !fund || busy}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <FileUp />}
            {busy ? "Parsing…" : "Upload and parse"}
          </Button>
        </div>

        <p className="mt-3 text-[11px] text-muted-foreground">
          Your DDQ is processed against the GreenPoint data room. Its contents
          are not shared with other LPs.
        </p>
      </div>
    </div>
  );
}
